import { Loading } from 'components/common/Loading';
import { PageHelmet } from 'components/common/PageHelmet';
import { useCommonTranslation } from 'hooks/i18n/useCommonTranslation';
import { usePublicSite } from 'hooks/public/usePublicSite';
import { KioskPreCheckinQuestion } from './components/KioskPreCheckinQuestion';
import { KioskLoading } from './components/KioskLoading';
import {
  KIOSK_APPT_SELECT,
  KIOSK_BIRTH_MONTH_INPUT,
  KIOSK_CHECKIN_SUCCESS,
  KIOSK_EARLY_ERROR,
  KIOSK_GENERAL_ERROR,
  KIOSK_LATE_ERROR,
  KIOSK_LOADING_STATES,
  KIOSK_MULTIPLE_RESULTS_ERROR,
  KIOSK_NO_APPTS_FOUND_ERROR,
  KIOSK_UPDATE_USER,
  KIOSK_PHONE_INPUT,
  KIOSK_PRE_CHECKIN_QUESTION,
  isKioskBirthDayInput,
  isKioskSurnameInitialInput,
} from 'hooks/kiosk/useKioskState';
import { KioskError } from './components/KioskError';
import { useKiosk } from 'hooks/kiosk/useKiosk';
import { KioskApptSelect } from './components/KioskApptSelect';
import { KioskCheckinSuccess } from './components/KioskCheckinSuccess';
import { KioskEntryPhone } from './components/KioskEntryPhone';
import { KioskUpdateUser } from './components/KioskUpdateUser';
import { KioskEntryBirthMonth } from './components/KioskEntryBirthMonth';
import { KioskEntryBirthDay } from './components/KioskEntryBirthDay';
import { KioskEntrySurnameInitial } from './components/KioskEntrySurnameInitial';
import { useReloadOnVersionUpdate } from 'hooks/version/useReloadOnVersionUpdate';
import { KioskRootFontSize } from './Kiosk.styled';

export const Kiosk = () => {
  useReloadOnVersionUpdate({ trigger: 'time' });
  const { isReady } = usePublicSite();
  /**
   * Until isReady = true useSite hook returns undefined. This can cause
   * issues, as in non-public site environment it is available from context
   * straight away and is typed as defined, that's why there is
   * the loading element first.
   */
  return (
    <>
      <KioskRootFontSize />
      {isReady ? <KioskSiteLoaded /> : <Loading />}
    </>
  );
};

const KioskSiteLoaded = () => {
  const { t } = useCommonTranslation();
  const {
    kioskState,
    kioskApptSearchInput,
    appt,
    setInputState,
    showBirthDayInput,
    selectBirthDay,
    backToBirthMonth,
    searchBySurnameInitial,
    searchByPhone,
    onApptSelected,
    reset,
    resetEvent,
    updateUserAndCheckin,
    onUserActivity,
  } = useKiosk();

  return (
    <>
      <PageHelmet title={t('kiosk-tab-title')} />

      {kioskState === KIOSK_PRE_CHECKIN_QUESTION && (
        <KioskPreCheckinQuestion
          onPreCheckinFinished={() => setInputState('BIRTHDATE')}
        />
      )}

      {kioskState === KIOSK_BIRTH_MONTH_INPUT && (
        <KioskEntryBirthMonth
          onMonthSelected={showBirthDayInput}
          onDifferentInputClicked={setInputState}
        />
      )}

      {isKioskBirthDayInput(kioskState) && (
        <KioskEntryBirthDay
          month={kioskState.month}
          onDaySelected={(day) =>
            selectBirthDay({ month: kioskState.month, day })
          }
          onBackClicked={backToBirthMonth}
        />
      )}

      {isKioskSurnameInitialInput(kioskState) && (
        <KioskEntrySurnameInitial
          onSurnameInitialSubmitted={(surnameInitial) =>
            searchBySurnameInitial(kioskState, surnameInitial)
          }
          onBackClicked={backToBirthMonth}
          onUserActivity={onUserActivity}
        />
      )}

      {kioskState === KIOSK_PHONE_INPUT && (
        <KioskEntryPhone
          onPhone={searchByPhone}
          onDifferentInputClicked={setInputState}
          onUserActivity={onUserActivity}
          resetEvent={resetEvent}
        />
      )}

      {kioskState === KIOSK_APPT_SELECT && (
        <KioskApptSelect
          onExitClicked={reset}
          appts={appt ? [appt] : []}
          onApptSelected={onApptSelected}
        />
      )}

      {kioskState === KIOSK_UPDATE_USER && (
        <KioskUpdateUser
          onUpdateUser={updateUserAndCheckin}
          onExitClicked={reset}
        />
      )}

      {kioskState === KIOSK_CHECKIN_SUCCESS && (
        <KioskCheckinSuccess onExitClicked={reset} />
      )}

      {KIOSK_LOADING_STATES.includes(kioskState) && <KioskLoading />}

      {(kioskState === KIOSK_EARLY_ERROR ||
        kioskState === KIOSK_LATE_ERROR ||
        kioskState === KIOSK_NO_APPTS_FOUND_ERROR ||
        kioskState === KIOSK_GENERAL_ERROR ||
        kioskState === KIOSK_MULTIPLE_RESULTS_ERROR) && (
        <KioskError
          onExitClicked={reset}
          kioskState={kioskState}
          searchParams={kioskApptSearchInput}
        />
      )}
    </>
  );
};
